import { AbortUpdateFn } from 'src/common/helpers/file-upload';
import { Content } from 'src/common/interfaces/content.interface';
import {
  apiCreateContent,
  apiCreateHtmlArtifactFromSource,
  apiUploadHtmlArtifact,
} from 'src/utils/journeyApi';
import { HtmlBlockContent } from '../../types';

// 5 MB. Comfortably above any single-file Claude artifact — those run to a few
// hundred KB even with a chart library inlined. Enforced again server-side;
// this copy exists only so the user gets an error before a long upload rather
// than after it.
export const HTML_BLOCK_MAX_BYTES = 5 * 1024 * 1024;

export const HTML_BLOCK_ACCEPTED_MIME_TYPES = ['text/html'];

// Used when the embedded page never reports its height over postMessage, which
// is the common case: a Claude artifact has no reason to know it is in a
// Journey. Tall enough that a typical artifact is usable without scrolling,
// short enough that it does not leave a chasm of whitespace when the content is
// small. Revisit against real artifacts — see HTML_BLOCKS_PLAN.md.
export const HTML_BLOCK_FALLBACK_HEIGHT = 600;

// Ignore anything taller than this even if the page asks for it. A page can
// report its own height, and a hostile or buggy one could report 10^9 to push
// the rest of the Journey off screen.
export const HTML_BLOCK_MAX_REPORTED_HEIGHT = 10000;
export const HTML_BLOCK_MIN_REPORTED_HEIGHT = 100;

// The sandbox for the rendering iframe. Deliberately a constant rather than an
// inline string: this is a security boundary, and it should be greppable and
// changed in one place.
//
//   allow-scripts  — the point of the feature. Claude artifacts are interactive;
//                    without this they render as broken static pages.
//   allow-popups   — lets a link in the artifact open in a new tab.
//
// Deliberately excluded:
//   allow-same-origin     — would defeat the isolation entirely. With it, the
//                           frame could reach Journey's origin. Never add this.
//   allow-top-navigation  — would let embedded content redirect the whole
//                           Journey away from Journey. A phishing primitive.
//   allow-forms           — see below.
//
// On allow-forms (JOU-42 asks for a documented position): omitted for v1.
// Journey has no way to tell a legitimate form from a credential-harvesting one
// rendered on a page the recipient reached from a trusted sender. Allowing form
// submission from arbitrary customer HTML on a Journey-adjacent surface is the
// phishing risk JOU-42 names, and we cannot mitigate it by inspection.
//
// This is a one-way door in one direction only: adding allow-forms later is
// safe and additive, while removing it later breaks every artifact that had
// come to rely on it. Start closed.
export const HTML_BLOCK_IFRAME_SANDBOX = 'allow-scripts allow-popups';

export function isHtmlFile(file: File): boolean {
  return HTML_BLOCK_ACCEPTED_MIME_TYPES.includes(file.type);
}

// Mirrors the server's sniffing so an obvious paste mistake is caught without a
// round trip. The server check is the control — this one is only for the error
// message.
const HTML_SOURCE_SIGNATURES = ['<!doctype html', '<html', '<head', '<body', '<!--', '<meta', '<div', '<script', '<style'];

export function looksLikeHtmlSource(source: string): boolean {
  const head = source.replace(/^﻿/, '').trimStart().slice(0, 1024).toLowerCase();
  return HTML_SOURCE_SIGNATURES.some((signature) => head.startsWith(signature));
}

// Byte length, not string length: a multi-byte character costs more than one
// byte against the ceiling, and the server measures the encoded payload.
export function htmlSourceByteLength(source: string): number {
  return new TextEncoder().encode(source).length;
}

// Clamp a height reported by the embedded page. Returns null when the page has
// not reported a usable one, which tells the renderer to keep its fallback.
export function clampReportedHeight(reported: unknown): number | null {
  if (typeof reported !== 'number' || !Number.isFinite(reported)) {
    return null;
  }
  const rounded = Math.round(reported);
  if (rounded < HTML_BLOCK_MIN_REPORTED_HEIGHT) {
    return HTML_BLOCK_MIN_REPORTED_HEIGHT;
  }
  if (rounded > HTML_BLOCK_MAX_REPORTED_HEIGHT) {
    return HTML_BLOCK_MAX_REPORTED_HEIGHT;
  }
  return rounded;
}

// Uploaded through the API rather than the browser-direct S3 path that images
// and PDFs use. That path hands the browser federation credentials and lets it
// choose its own Content-Type — fine for an image, not for a document we serve
// as text/html from an origin, where the client could bypass the size ceiling
// and the content sniffing entirely. The server decides what is stored here.
export async function uploadHtmlBlockFile(
  file: File,
  orgId: any,
  onProgress: (progress: number) => void,
  onAbort?: AbortUpdateFn
): Promise<Partial<HtmlBlockContent> | undefined> {
  const artifact = await apiUploadHtmlArtifact(orgId, file, onProgress, onAbort);
  const jnyContent: Content = await apiCreateContent(orgId, {
    file_url: artifact.url,
    file_content_type: artifact.content_type,
    name: artifact.name,
  });
  return {
    contentUUID: jnyContent.uuid,
    url: artifact.url,
  } as Partial<HtmlBlockContent>;
}

// The paste entry point. Goes to the same endpoint as the file upload, so a
// pasted artifact passes exactly the same validation and sanitization.
export async function createHtmlBlockFromSource(
  source: string,
  orgId: any,
  name?: string
): Promise<Partial<HtmlBlockContent> | undefined> {
  const artifact = await apiCreateHtmlArtifactFromSource(orgId, source, name);
  const jnyContent: Content = await apiCreateContent(orgId, {
    file_url: artifact.url,
    file_content_type: artifact.content_type,
    name: artifact.name,
  });
  return {
    contentUUID: jnyContent.uuid,
    url: artifact.url,
    metadata: { name: artifact.name, size: artifact.size },
  } as Partial<HtmlBlockContent>;
}
